import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import {
  ArrowRight, ArrowLeft, Check, Sparkles, ChevronDown,
  Server, Layers, Radio, Cloud, Play, Shield, CircleDollarSign, HardDrive, Cast, BarChart3, Cog,
} from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { SERVICES } from "@/lib/i18n";
import { SERVICE_DEEP, SERVICE_DEEP_LABELS } from "@/lib/serviceDeepContent";

const Icons = {
  "core-cms": Server, "front-apps": Layers, "live-streaming": Radio, "cloud-playout": Cloud,
  "transcoder": Sparkles, "dvr-catchup": Play, "secure": Shield, "monetization": CircleDollarSign,
  "stb": HardDrive, "casting": Cast, "analytics": BarChart3, "ingest": Cog,
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const [openFaq, setOpenFaq] = useState(0);
  const services = pickLang(SERVICES, lang);
  const service = services.find((s) => s.slug === slug);

  const deepLang = SERVICE_DEEP[lang] || SERVICE_DEEP.en;
  const deepEn = SERVICE_DEEP.en;
  const deep = slug ? (deepLang[slug] || deepEn[slug] || null) : null;
  const deepLabels = SERVICE_DEEP_LABELS[lang] || SERVICE_DEEP_LABELS.en;

  // FAQPage JSON-LD for SEO
  useEffect(() => {
    const scriptId = "service-faq-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (deep?.faqs?.length) {
      const jsonld = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: deep.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      };
      const s = document.createElement("script");
      s.id = scriptId;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(jsonld);
      document.head.appendChild(s);
    }
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [slug, lang, deep]);

  if (!service) return <Navigate to="/services" replace />;
  const Icon = Icons[slug] || Sparkles;

  // Related = next 3 services in the list (excluding current)
  const currentIdx = services.findIndex((s) => s.slug === slug);
  const related = [];
  for (let i = 1; i <= 3; i++) {
    related.push(services[(currentIdx + i) % services.length]);
  }

  const backLabel = lang === "fr" ? "Tous les services" : lang === "es" ? "Todos los servicios" : lang === "pt" ? "Todos os serviços" : "All services";
  const featuresLabel = lang === "fr" ? "Ce qui est inclus" : lang === "es" ? "Qué incluye" : lang === "pt" ? "O que inclui" : "What's included";
  const relatedLabel = lang === "fr" ? "Services associés" : lang === "es" ? "Servicios relacionados" : lang === "pt" ? "Serviços relacionados" : "Related services";

  return (
    <div data-testid="service-detail-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-28">
          <Link to="/services" data-testid="service-back" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} /> {backLabel}
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center text-black">
                <Icon size={30} strokeWidth={2.2} />
              </div>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] text-white">
                {service.title}
              </h1>
              <p className="mt-6 md:mt-8 text-base md:text-xl text-slate-200 leading-relaxed max-w-2xl">
                {service.summary}
              </p>
              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3">
                <Link to="/demo" data-testid="service-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
                <Link to="/contact" data-testid="service-cta-contact" className="btn-ghost">{t("cta.talk_sales")}</Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl gradient-bg opacity-20 blur-3xl" />
                <div className="relative glass rounded-3xl aspect-[4/3] overflow-hidden p-6 md:p-8 flex flex-col justify-between">
                  <div className="bg-grid absolute inset-0 opacity-40" />
                  <div className="relative">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {`${(currentIdx + 1).toString().padStart(2, "0")} / ${services.length.toString().padStart(2, "0")}`}
                    </div>
                    <div className="mt-3 font-display text-2xl md:text-3xl font-bold text-slate-100 tracking-tight">{service.title}</div>
                  </div>
                  <div className="relative grid grid-cols-2 gap-2 md:gap-3">
                    {service.bullets.map((b, k) => (
                      <div key={k} className="rounded-xl border border-white/10 bg-white/[0.06] p-3 text-xs text-slate-200">
                        {b}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline">{featuresLabel}</div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {service.bullets.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass rounded-2xl p-6 md:p-7 flex gap-4 items-start"
                data-testid={`service-feature-${i}`}
              >
                <span className="mt-1 w-6 h-6 rounded-full gradient-bg flex items-center justify-center shrink-0">
                  <Check size={13} strokeWidth={3} className="text-black" />
                </span>
                <div>
                  <div className="font-display font-semibold text-white text-lg">{b}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview - long description */}
      {deep?.longDescription && (
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="overline">{deepLabels.overview}</div>
            <div className="mt-8 space-y-5">
              {deep.longDescription.map((para, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="text-base md:text-lg text-slate-200 leading-relaxed"
                  data-testid={`service-overview-para-${i}`}
                >
                  {para}
                </motion.p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use cases */}
      {deep?.useCases && deep.useCases.length > 0 && (
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="overline">{deepLabels.useCases}</div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {deep.useCases.map((u, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="glass rounded-2xl p-6 md:p-7 flex flex-col h-full"
                  data-testid={`service-usecase-${i}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1FE0A8]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display mt-4 text-xl md:text-2xl font-bold tracking-tight text-white">{u.title}</h3>
                  <p className="mt-3 text-sm md:text-base text-slate-200 leading-relaxed">{u.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {deep?.faqs && deep.faqs.length > 0 && (
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="overline">{deepLabels.faqs}</div>
            <div className="mt-10 divide-y divide-white/10 rounded-2xl glass overflow-hidden">
              {deep.faqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} data-testid={`service-faq-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : i)}
                      className="w-full flex items-start justify-between gap-4 p-5 md:p-6 text-left hover:bg-white/[0.03] transition-colors"
                      data-testid={`service-faq-toggle-${i}`}
                      aria-expanded={isOpen}
                    >
                      <span className="font-display text-base md:text-lg font-semibold text-white">{f.q}</span>
                      <ChevronDown
                        size={20}
                        className={`shrink-0 mt-0.5 text-slate-300 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-sm md:text-base text-slate-200 leading-relaxed">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      <section className="py-16 md:py-24 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline">{relatedLabel}</div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white mb-10 md:mb-12">
            {t("cta.explore_services")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => {
              const RIcon = Icons[r.slug] || Sparkles;
              return (
                <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`service-related-${r.slug}`} className="card-hover glass rounded-2xl p-6 md:p-7 flex flex-col group">
                  <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center text-black">
                    <RIcon size={22} strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display mt-5 text-xl font-bold tracking-tight text-white group-hover:text-[#1FA8FF] transition-colors">{r.title}</h3>
                  <p className="mt-3 text-sm text-slate-200 leading-relaxed line-clamp-3">{r.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {lang === "fr" ? <>Prêt à intégrer <span className="gradient-text">{service.title}</span> ?</>
            : lang === "es" ? <>¿Listo para integrar <span className="gradient-text">{service.title}</span>?</>
            : lang === "pt" ? <>Pronto para integrar <span className="gradient-text">{service.title}</span>?</>
            : <>Ready to ship <span className="gradient-text">{service.title}</span>?</>}
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="service-bottom-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="service-bottom-cta-contact" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
